import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { User } from '../../../user/entities/user';
import { UserRoles } from '../../../user/entities/user-roles';
import { DateFormat, ItemType, TableConfig, TableItem, TableUpdateValue } from '../../../table/entities/table';
import { Note } from '../../entities/note';
import { ModalController } from '@ionic/angular';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';
import { NoteService } from '../../services/note.service';
import { PaginatedResponse, SortBy } from '../../../common/entities/paginated-response';
import { StringItemAdapterComponent } from '../../../table/components/table-adapter/string-item-adapter.component';
import { NoteDetailComponent } from '../note-detail/note-detail.component';
import { TagsAdapterComponent } from '../../../therapy/components/table-adapter/tags-adapter.component';
import {
    IconAdapterComponent,
    IconAdapterElement,
} from '../../../table/components/table-adapter/icon-adapter.component';
import { IconType } from '../../../common/entities/icon_type';
import { UserPreferencesService } from '../../../user/services/user-preferences/user-preferences.service';
import { distinctUntilChanged, filter, map, shareReplay, skip, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, merge, Subject } from 'rxjs';
import { isEqual } from 'lodash-es';
import { CarePlanerFilterModalComponent } from '../../../common/components/patient-dossier/care-planer/care-planer-filter-modal/care-planer-filter-modal.component';
import { NoteTagFilterPipe } from '../../pipes/note-tag-filter.pipe';
import { CurafidaTableComponent } from '../../../table/components/curafida-table/curafida-table.component';
import { NoteSortProperty, NoteTag } from '../../entities/note-tags.model';
import { NoteFilterSetting } from '../../entities/note-filter.setting';
import { NoteTagsService } from '../../services/note-tags.service';
import { CurafidaSearchAndButtonComponent } from '../../../common/components/curafida-search-and-button/curafida-search-and-button.component';
import {
    CurafidaSearchAndButtonEvent,
    CurafidaSearchAndButtonEventAction,
    CurafidaSearchAndButtonUpdateFilter,
} from '../../../common/components/curafida-search-and-button/model/curafida-search-and-button.model';
import { Logger, LoggingService } from '../../../logging/logging.service';

/** Name the note list of a patient saves its filter and table state under in the user preferences. */
export const noteListFilterSettingName = 'NoteListComponent';

function filterCriteriaOf(filterSetting: NoteFilterSetting | undefined): Pick<NoteFilterSetting, 'tags'> {
    return { tags: filterSetting?.tags };
}

@Component({
    selector: 'lib-note-list',
    templateUrl: './note-list.component.html',
    styleUrls: ['./note-list.component.scss'],
    imports: [CurafidaTableComponent, CurafidaSearchAndButtonComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [NoteTagFilterPipe],
})
export class NoteListComponent implements OnInit {
    private readonly noteService = inject(NoteService);
    private readonly authService = inject(CurafidaAuthService);
    private readonly modalController = inject(ModalController);
    private readonly userPreferencesService = inject(UserPreferencesService);
    readonly noteTagsService = inject(NoteTagsService);
    private readonly loggingService = inject(LoggingService);
    private readonly destroyRef = inject(DestroyRef);

    readonly isLoading = signal(true);
    readonly isLoadingSuccess = signal(false);
    readonly user = input<User>();
    readonly filterSettingName = input(noteListFilterSettingName);
    readonly sharedFilterSettingName = input<string>();
    /** Emits after the user changed notes through this list, so another list of them can reload */
    readonly notesChanged = output<void>();
    userRoles: UserRoles[] = [];
    UserRoles = UserRoles;
    noteTableConfig: TableConfig<Note[]> = new TableConfig();
    addNoteButtonIcon = '';

    /** Reloads the table without touching the saved setting, e.g. after a note was written. */
    protected readonly reloadTable$ = new Subject<void>();
    protected readonly log: Logger;
    /** The state the list was last loaded with, i.e. what a partial update is merged into. */
    protected previousFilterSetting: NoteFilterSetting;
    /** What an account that never filtered notes starts with; sorting is left to the endpoint. */
    protected readonly defaultFilterSettings: NoteFilterSetting = {
        name: noteListFilterSettingName,
        offset: 0,
        limit: 10,
        searchTerm: '',
        tags: [],
        sortBy: undefined,
        sortByArray: undefined,
        sortOrder: undefined,
        filter: undefined,
    };
    /**
     * The saved state of this list, with the tags of the shared setting where one is given, written back with its
     * defaults filled in as it is read first.
     */
    protected readonly filterSettings$ = combineLatest([
        toObservable(this.filterSettingName),
        toObservable(this.sharedFilterSettingName),
    ]).pipe(
        switchMap(([name, sharedName]) => {
            const ownSetting$ = this.userPreferencesService.settingByName<NoteFilterSetting>(name);
            if (!sharedName) return ownSetting$;
            return combineLatest([
                ownSetting$,
                this.userPreferencesService.settingByName<NoteFilterSetting>(sharedName),
            ]).pipe(
                // Without a setting of its own yet, the list first saves its defaults, just like an unshared one
                map(([own, shared]) => (own ? { ...own, ...filterCriteriaOf(shared) } : undefined)),
            );
        }),
        tap((filterSetting) => {
            if (this.previousFilterSetting) return;
            const initialFilterSetting = { ...this.defaultFilterSettings, ...filterSetting };
            this.previousFilterSetting = structuredClone(filterSetting);
            /* A search term narrows the list for a moment, so a new visit starts without it. */
            if (filterSetting?.searchTerm) {
                initialFilterSetting.searchTerm = '';
                this.upsertFilterSetting(initialFilterSetting, true);
            } else {
                this.upsertFilterSetting(initialFilterSetting);
            }
        }),
        // One subscription for all readers, so the defaults above are saved once and not once per reader
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    /** What the filter button of the list shows: the tags a note has to carry. */
    protected readonly filterCount = toSignal(this.filterSettings$.pipe(map((it) => it?.tags?.length ?? 0)), {
        initialValue: 0,
    });
    /** Only emits the first filter setting and the changes to it, so a reload has something new to show. */
    protected readonly filterSettingChanges$ = this.filterSettings$.pipe(
        filter((filterSetting) => !!filterSetting),
        map((filterSetting) => this.startOverOnChangedCriteria(filterSetting)),
        // Previous emit is not working correctly, so we use local backup
        distinctUntilChanged((_previous, current) => isEqual(this.previousFilterSetting, current)),
        tap((filterSetting) => {
            this.previousFilterSetting = structuredClone(filterSetting);
        }),
    );
    private detailModalOpened = false;
    private filterModalOpened = false;

    constructor() {
        this.log = this.loggingService.getLogger(this.constructor.name);
        this.initTable();
    }

    async ngOnInit(): Promise<void> {
        this.userRoles = this.authService.getSession()?.user?.roles ?? ([] as UserRoles[]);
        this.addNoteButtonIcon = this.canCreateNote() ? 'add' : '';
        this.initFilterSettings();
        this.dropVanishedTags();
        this.noteTagsService.init(true);
    }

    /**
     * Merges the given values into the saved state, which reloads the list through {@link filterSettings$}.
     *
     * @param value - the part of the state that changed
     * @param resetOffset - whether the list jumps back to its first page, as a narrower filter needs
     */
    async upsertFilterSetting(value: Partial<NoteFilterSetting>, resetOffset: boolean = false): Promise<void> {
        const upsertValue = structuredClone(this.previousFilterSetting ?? this.defaultFilterSettings);

        /* First filter out null/undefined entries */
        const validUpdates = Object.fromEntries(
            Object.entries(value).filter(([_, v]) => v !== null && v !== undefined),
        );

        /* Second merge them. TypeScript handles Object.assign safely even with partials. */
        Object.assign(upsertValue, validUpdates);
        upsertValue.name = this.filterSettingName();

        if (resetOffset) {
            upsertValue.offset = 0;
        }
        if (!isEqual(this.previousFilterSetting, upsertValue)) {
            this.isLoading.set(true);
            this.isLoadingSuccess.set(false);
            this.saveFilterSetting(upsertValue);
        }
    }

    canCreateNote(): boolean {
        return this.userRoles.includes(UserRoles.write_note) || this.userRoles.includes(UserRoles.SUPERVISOR);
    }

    async openNoteDetail(note?: Note): Promise<void> {
        if (this.detailModalOpened) {
            return;
        }
        this.detailModalOpened = true;
        const modal = await this.modalController.create({
            component: NoteDetailComponent,
            cssClass: 'full-width-modal',
            componentProps: {
                noteUuid: note?.uuid,
                patient: this.user(),
                isNew: !note,
            },
            backdropDismiss: false,
        });
        await modal.present();
        await modal.onDidDismiss().then(() => (this.detailModalOpened = false));
        this.reloadTable$.next();
        this.notesChanged.emit();
    }

    /** Fetches the notes anew without touching the saved setting, e.g. after another list of them changed one. */
    reload(): void {
        this.reloadTable$.next();
    }

    /** Fetches the page of notes the given state describes. */
    /**
     * Fetches the notes the given state describes. Writes nothing itself, so a superseded load can be
     * dropped by the caller; a failed fetch resolves to an empty list instead of rejecting.
     */
    async getNotes(filterSetting: NoteFilterSetting): Promise<{ list: PaginatedResponse<Note[]>; success: boolean }> {
        try {
            const list = await this.noteService.fetchNotes({
                concernedUsername: this.user().username,
                offset: filterSetting?.offset,
                limit: filterSetting?.limit,
                searchTerm: filterSetting?.searchTerm,
                tagUuids: filterSetting?.tags?.map((it) => it.uuid),
                sortOrder: filterSetting?.sortOrder,
                /* The note endpoints sort by their own properties, which the shared setting cannot type. */
                sortBy: filterSetting?.sortBy as unknown as NoteSortProperty,
            });
            list.items.forEach((it: Note & { icons?: IconAdapterElement[] }) => {
                it['icons'] = [];
                if (it.isTracked) {
                    it['icons'].push({
                        type: IconType.ION_ICON,
                        name: 'flag',
                        color: 'danger',
                    });
                }
                if (it.isVisibleForSupervisor === false) {
                    it['icons'].push({
                        type: IconType.ION_ICON,
                        name: 'eye-off',
                        color: 'primary',
                    });
                }
            });
            return { list, success: true };
        } catch (err) {
            this.log.error('[getNotes] error');
            this.log.error(err);
            return { list: new PaginatedResponse<Note[]>(), success: false };
        }
    }

    /** Keeps the page and the sorting the user chose in the table. */
    async processTableUpdate(value: TableUpdateValue): Promise<void> {
        await this.upsertFilterSetting({
            offset: value?.offset,
            limit: value?.limit,
            sortBy: value?.sortBy as SortBy,
            sortOrder: value?.sortOrder,
        });
    }

    /** Keeps what the search bar of the list holds. */
    async processFilterUpdate(value: CurafidaSearchAndButtonEvent): Promise<void> {
        switch (value.action) {
            case CurafidaSearchAndButtonEventAction.UPDATE_FILTER: {
                const changes = value.payload as CurafidaSearchAndButtonUpdateFilter;
                await this.upsertFilterSetting({ searchTerm: changes.searchTerm ?? '' }, true);
                break;
            }
        }
    }

    async openFilterModal(): Promise<void> {
        if (this.filterModalOpened) {
            return;
        }
        this.filterModalOpened = true;
        const modal = await this.modalController.create({
            component: CarePlanerFilterModalComponent,
            componentProps: {
                mode: 'NOTE',
                filterSettingName: this.sharedFilterSettingName() ?? this.filterSettingName(),
                initialNoteTags: this.previousFilterSetting?.tags ?? [],
            },
        });
        await modal.present();
        await modal.onDidDismiss().then(async ({ data }) => {
            this.filterModalOpened = false;
            /* Dismissed through the toolbar instead of the apply button: nothing was picked. */
            if (data) await this.upsertFilterSetting({ tags: data as NoteTag[] }, true);
        });
    }

    /**
     * Saves the setting under {@link filterSettingName}, and its tags into the shared setting when there is one. Both
     * go out in one update: each update sends all preferences, so a second one would undo the first.
     */
    private saveFilterSetting(filterSetting: NoteFilterSetting): void {
        const sharedName = this.sharedFilterSettingName();
        if (!sharedName) {
            this.userPreferencesService.updateFilterSettingItem(filterSetting);
            return;
        }
        const sharedSetting = this.userPreferencesService
            .userPreferences()
            ?.filterSettings?.filterSettingItem?.find((item) => item.name === sharedName);
        this.userPreferencesService.updateFilterSettingItems([
            filterSetting,
            { ...(sharedSetting ?? new NoteFilterSetting(sharedName)), ...filterCriteriaOf(filterSetting) },
        ]);
    }

    /**
     * Tags changed by the other list of a shared setting show their first page, not the page this list was on, which
     * the narrowed list may not even have. The first page is saved too, or the next emission of the preferences would
     * bring the old page back.
     */
    private startOverOnChangedCriteria(filterSetting: NoteFilterSetting): NoteFilterSetting {
        const previous = this.previousFilterSetting;
        if (!previous || !filterSetting.offset) return filterSetting;
        if (isEqual(filterCriteriaOf(previous), filterCriteriaOf(filterSetting))) return filterSetting;

        const firstPage = { ...filterSetting, offset: 0 };
        this.userPreferencesService.updateFilterSettingItem(firstPage);
        return firstPage;
    }

    /** Every change to the saved state, and every reload emit, fetches the notes anew. */
    private initFilterSettings(): void {
        merge(this.filterSettingChanges$, this.reloadTable$.pipe(map(() => this.previousFilterSetting)))
            .pipe(
                tap(() => {
                    this.isLoading.set(true);
                    this.isLoadingSuccess.set(false);
                }),
                /* Only the latest load may reach the table; a slower, superseded one is dropped. */
                switchMap((filterSetting) => this.getNotes(filterSetting)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ list, success }) => {
                this.noteTableConfig.list = list;
                this.isLoadingSuccess.set(success);
                this.isLoading.set(false);
            });
    }

    /**
     * Drops saved tags that meanwhile ceased to exist, so a deleted one stops narrowing the list
     * invisibly. Only writes when it found something, since that write reloads the list.
     */
    private dropVanishedTags(): void {
        this.noteTagsService.allTags$
            .pipe(skip(1), take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((tagsFromServer) => {
                const savedTags = this.previousFilterSetting?.tags ?? [];
                const tags = savedTags.filter((saved) => tagsFromServer?.some((tag) => tag.uuid === saved.uuid));
                if (tags.length !== savedTags.length) this.upsertFilterSetting({ tags }, true);
            });
    }

    private initTable() {
        this.noteTableConfig.emptyListLabel = 'NOTE.ANY';
        this.noteTableConfig.itemSettings = [
            TableItem.create({
                id: 'updated_at',
                prop: 'updated_at',
                header: 'NOTE.UPDATED_AT',
                adapter: StringItemAdapterComponent,
                format: DateFormat.DATE_AND_HOUR_2_LINES,
                type: ItemType.ADAPTER,
                width: 'max(15%, 105px)',
                columnPosition: 0,
                sortBy: NoteSortProperty.UPDATED_AT,
            }),
            TableItem.create({
                id: 'editorFullName',
                prop: 'editorFullName',
                header: 'CREATOR',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '15%',
                columnPosition: 1,
                sortBy: NoteSortProperty.EDITOR_FULL_NAME,
            }),
            TableItem.create({
                id: 'text',
                prop: 'text',
                header: 'TEXT',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: 'max(60px, calc(51% - max(15%, 105px)))',
                columnPosition: 2,
                maxTextLines: 3,
            }),
            TableItem.create({
                id: 'tags',
                prop: 'tags',
                header: 'TAG.PLURAL',
                adapter: TagsAdapterComponent,
                type: ItemType.ADAPTER,
                width: '28%',
                columnPosition: 3,
            }),
            TableItem.create({
                id: 'icon',
                prop: 'icons',
                header: '',
                type: ItemType.ADAPTER,
                adapter: IconAdapterComponent,
                width: '3%',
                columnPosition: 4,
            }),
        ];
        this.noteTableConfig.isOpenDetailEnable = true;
    }
}
